import React, { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { GET_COUPONS, UPDATE_COUPON } from '../../services/couponService/queries';
import CouponForm from './CouponForm';
import CouponList from './CouponList';
import { authStore } from '../../../store/authStore';
import CouponsPageSkeleton from '../../components/skeletons/CouponsPageSkeleton';

const PageContainer = styled.div`
  ${tw`w-full max-w-full min-w-0 box-border px-4 py-8`}
`;

const ContentContainer = styled.div`
  ${tw`mx-auto w-full max-w-full min-w-0 max-w-screen-xl box-border`}
`;

const Header = styled.div`
  ${tw`flex justify-between items-center mb-6`}
`;

const Title = styled.h1`
  ${tw`text-2xl font-bold text-text-primary`}
`;

const AddButton = styled.button`
  ${tw`px-4 py-2 bg-black text-white rounded-md transition max-w-full min-w-0 box-border`}
  &:hover {
    ${tw`bg-black`}
  }
`;

export default function CouponsPage() {
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<any>(null);
  const user = authStore.user;

  const { data, loading, error, refetch } = useQuery(GET_COUPONS, {
    skip: !user,
    onError: err => {
      toast.error(`Failed to load coupons: ${err.message}`);
    },
  });

  const [expireCoupon] = useMutation(UPDATE_COUPON, {
    refetchQueries: [GET_COUPONS],
    onCompleted: () => {
      toast.success('Coupon expired successfully');
    },
    onError: err => {
      toast.error(`Failed to expire coupon: ${err.message}`);
    },
  });

  const handleAdd = () => {
    setEditingCoupon(null);
    setShowForm(true);
  };

  const handleEdit = (coupon: any) => {
    setEditingCoupon(coupon);
    setShowForm(true);
  };

  const handleExpire = async (id: string) => {
    if (
      window.confirm('Are you sure you want to expire this coupon? It will be set to inactive.')
    ) {
      try {
        await expireCoupon({ variables: { input: { id, isActive: false } } });
      } catch (err) {}
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingCoupon(null);
    refetch();
  };

  if (loading) {
    return <CouponsPageSkeleton />;
  }

  if (error) {
    return (
      <PageContainer>
        <ContentContainer>
          <div>Error: {error.message}</div>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentContainer>
        <Header>
          <Title>Coupons</Title>
          <AddButton onClick={handleAdd}>Add Coupon</AddButton>
        </Header>

        {showForm ? (
          <CouponForm
            coupon={editingCoupon}
            onClose={handleFormClose}
            onSuccess={handleFormClose}
          />
        ) : (
          <CouponList coupons={data?.coupons || []} onEdit={handleEdit} onExpire={handleExpire} />
        )}
      </ContentContainer>
    </PageContainer>
  );
}
